import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import {
  Loader2, Shield, ShieldAlert, AlertTriangle, ChevronRight,
  Search, FileText, Zap, Eye, Target, Building2, User
} from "lucide-react";
import { API } from "@/constants";
import SmartSelect from "@/components/shared/SmartSelect";

const InsightList = ({ items, icon: Icon = ChevronRight, color = "text-blue-500" }) => (
  <div className="space-y-2">
    {(items || []).map((item, i) => (
      <div key={i} className="flex items-start gap-2.5 text-sm">
        <Icon className={`h-4 w-4 mt-0.5 flex-shrink-0 ${color}`} />
        <span className="text-slate-700">{typeof item === "string" ? item : item.flag || item.detail || JSON.stringify(item)}</span>
      </div>
    ))}
  </div>
);

const RiskGauge = ({ score, label }) => {
  const color = score >= 70 ? "text-red-500" : score >= 40 ? "text-amber-500" : "text-green-500";
  const bg = score >= 70 ? "bg-red-50" : score >= 40 ? "bg-amber-50" : "bg-green-50";
  return (
    <div className={`flex flex-col items-center gap-1 p-4 rounded-2xl ${bg}`}>
      <span className={`text-4xl font-black ${color}`}>{score}</span>
      <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</span>
    </div>
  );
};

const ENTITY_OPTS = [
  { value: "individual", label: "Individual" },
  { value: "company", label: "Company / Corporation" },
  { value: "llc", label: "LLC" },
  { value: "trust", label: "Trust" },
  { value: "partnership", label: "Partnership" },
  { value: "shell", label: "Shell Entity" },
  { value: "offshore", label: "Offshore Entity" },
];

const COUNTRY_OPTS = [
  { value: "US", label: "United States" }, { value: "UK", label: "United Kingdom" },
  { value: "Canada", label: "Canada" }, { value: "Mexico", label: "Mexico" },
  { value: "China", label: "China" }, { value: "Russia", label: "Russia" },
  { value: "Iran", label: "Iran" }, { value: "Cuba", label: "Cuba" },
];

// ─── SelleShield Tab ───────────────────────────────────────
const SelleShieldTab = () => {
  const [form, setForm] = useState({ entity_name: "", entity_type: "individual", jurisdiction: "", description: "", financial_amount: 0, industry: "", known_associates: "", prior_litigation: 0, years_in_business: 0 });
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const run = async () => {
    if (!form.entity_name) return;
    setLoading(true);
    try {
      const r = await fetch(`${API}/triple-shield/selleshield`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, financial_amount: Number(form.financial_amount), prior_litigation: Number(form.prior_litigation), years_in_business: Number(form.years_in_business), known_associates: form.known_associates ? form.known_associates.split(",").map(s => s.trim()) : [] })
      });
      const j = await r.json();
      setData(j.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const ai = data?.ai_analysis;

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div><Label className="text-xs font-semibold">Entity Name *</Label><Input value={form.entity_name} onChange={e => setForm({ ...form, entity_name: e.target.value })} placeholder="John Smith / Acme Corp" className="mt-1" data-testid="ss-entity" /></div>
        <div><Label className="text-xs font-semibold">Entity Type</Label><div className="mt-1"><SmartSelect value={form.entity_type} onValueChange={v => setForm({ ...form, entity_type: v })} options={ENTITY_OPTS} placeholder="Select..." allowCustom={false} /></div></div>
        <div><Label className="text-xs font-semibold">Jurisdiction</Label><Input value={form.jurisdiction} onChange={e => setForm({ ...form, jurisdiction: e.target.value })} placeholder="California, US" className="mt-1" /></div>
        <div><Label className="text-xs font-semibold">Industry</Label><Input value={form.industry} onChange={e => setForm({ ...form, industry: e.target.value })} placeholder="Real estate, Finance..." className="mt-1" /></div>
        <div><Label className="text-xs font-semibold">Financial Amount ($)</Label><Input type="number" value={form.financial_amount} onChange={e => setForm({ ...form, financial_amount: e.target.value })} className="mt-1" /></div>
        <div><Label className="text-xs font-semibold">Years in Business</Label><Input type="number" value={form.years_in_business} onChange={e => setForm({ ...form, years_in_business: e.target.value })} className="mt-1" /></div>
        <div><Label className="text-xs font-semibold">Prior Litigation Count</Label><Input type="number" value={form.prior_litigation} onChange={e => setForm({ ...form, prior_litigation: e.target.value })} className="mt-1" /></div>
        <div><Label className="text-xs font-semibold">Known Associates (comma-separated)</Label><Input value={form.known_associates} onChange={e => setForm({ ...form, known_associates: e.target.value })} placeholder="Jane Doe, Bob Corp..." className="mt-1" /></div>
      </div>
      <div><Label className="text-xs font-semibold">Description / Context</Label><Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Any additional context..." className="mt-1 min-h-[60px]" /></div>
      <Button onClick={run} disabled={loading || !form.entity_name} className="w-full bg-gradient-to-r from-red-600 to-orange-600 hover:opacity-90 h-11" data-testid="ss-scan-btn">
        {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Scanning...</> : <><ShieldAlert className="mr-2 h-4 w-4" />Run SelleShield Scan</>}
      </Button>

      {data && (
        <div className="space-y-5 animate-in fade-in duration-500">
          <div className="rounded-2xl p-6 text-white" style={{ background: data.risk_level === "critical" ? "linear-gradient(135deg,#7F1D1D,#DC2626)" : data.risk_level === "high" ? "linear-gradient(135deg,#92400E,#F59E0B)" : data.risk_level === "medium" ? "linear-gradient(135deg,#78350F,#D97706)" : "linear-gradient(135deg,#065F46,#22C55E)" }}>
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs uppercase tracking-widest opacity-80">SelleShield Rating</p>
              <Badge className="bg-white/20 text-white text-lg font-black px-3">{data.shield_rating}</Badge>
            </div>
            <div className="flex items-center justify-center gap-6">
              <p className="text-6xl font-black">{data.risk_score}</p>
              <div className="text-sm opacity-80"><p>/ 100</p><p className="capitalize">{data.risk_level} Risk</p></div>
            </div>
          </div>
          {data.risk_factors?.length > 0 && (
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-2"><CardTitle className="text-sm">Risk Factors</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {data.risk_factors.map((f, i) => (
                    <div key={i} className="flex items-center justify-between p-3 rounded-xl bg-slate-50">
                      <div><p className="text-sm font-medium">{f.factor}</p><p className="text-xs text-slate-500">{f.detail}</p></div>
                      <Badge className={f.score >= 20 ? "bg-red-100 text-red-700" : f.score >= 10 ? "bg-amber-100 text-amber-700" : "bg-green-100 text-green-700"}>{f.score}</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
          {ai && (
            <div className="space-y-4">
              {ai.risk_narrative && <Card className="border-0 shadow-sm bg-red-50"><CardContent className="pt-5"><p className="text-sm text-red-800">{ai.risk_narrative}</p></CardContent></Card>}
              <div className="grid md:grid-cols-2 gap-4">
                {ai.fraud_indicators && <Card className="border-0 shadow-sm border-l-4 border-l-red-500"><CardHeader className="pb-2"><CardTitle className="text-sm">Fraud Indicators</CardTitle></CardHeader><CardContent><InsightList items={ai.fraud_indicators} icon={AlertTriangle} color="text-red-500" /></CardContent></Card>}
                {ai.recommended_due_diligence && <Card className="border-0 shadow-sm border-l-4 border-l-blue-500"><CardHeader className="pb-2"><CardTitle className="text-sm">Due Diligence Steps</CardTitle></CardHeader><CardContent><InsightList items={ai.recommended_due_diligence} color="text-blue-500" /></CardContent></Card>}
                {ai.public_records_to_check && <Card className="border-0 shadow-sm border-l-4 border-l-amber-500"><CardHeader className="pb-2"><CardTitle className="text-sm">Records to Check</CardTitle></CardHeader><CardContent><InsightList items={ai.public_records_to_check} icon={Search} color="text-amber-500" /></CardContent></Card>}
                {ai.risk_mitigation && <Card className="border-0 shadow-sm border-l-4 border-l-emerald-500"><CardHeader className="pb-2"><CardTitle className="text-sm">Risk Mitigation</CardTitle></CardHeader><CardContent><InsightList items={ai.risk_mitigation} icon={Shield} color="text-emerald-500" /></CardContent></Card>}
              </div>
              {ai.red_flags && <Card className="border-0 shadow-sm"><CardHeader className="pb-2"><CardTitle className="text-sm">Red Flags</CardTitle></CardHeader><CardContent><div className="space-y-2">{ai.red_flags.map((f, i) => (<div key={i} className="flex items-center justify-between p-3 rounded-xl bg-slate-50"><span className="text-sm">{f.flag || f}</span><Badge className={`${(f.severity || "") === "high" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>{f.severity || "medium"}</Badge></div>))}</div></CardContent></Card>}
              {ai.confidence_score !== undefined && <div className="text-center"><Badge className="bg-slate-100 text-slate-600">AI Confidence: {ai.confidence_score}%</Badge></div>}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── AML Compliance Tab ────────────────────────────────────
const AMLTab = () => {
  const [form, setForm] = useState({ entity_name: "", entity_type: "individual", country: "US", transaction_amount: 0, transaction_type: "", source_of_funds: "", occupation: "", pep_status: false, description: "" });
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const run = async () => {
    if (!form.entity_name) return;
    setLoading(true);
    try {
      const r = await fetch(`${API}/triple-shield/aml-check`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, transaction_amount: Number(form.transaction_amount) })
      });
      const j = await r.json();
      setData(j.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const ai = data?.ai_analysis;

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div><Label className="text-xs font-semibold">Entity Name *</Label><Input value={form.entity_name} onChange={e => setForm({ ...form, entity_name: e.target.value })} placeholder="Name..." className="mt-1" data-testid="aml-entity" /></div>
        <div><Label className="text-xs font-semibold">Entity Type</Label><div className="mt-1"><SmartSelect value={form.entity_type} onValueChange={v => setForm({ ...form, entity_type: v })} options={ENTITY_OPTS} placeholder="Select..." allowCustom={false} /></div></div>
        <div><Label className="text-xs font-semibold">Country</Label><div className="mt-1"><SmartSelect value={form.country} onValueChange={v => setForm({ ...form, country: v })} options={COUNTRY_OPTS} placeholder="Select..." /></div></div>
        <div><Label className="text-xs font-semibold">Transaction Amount ($)</Label><Input type="number" value={form.transaction_amount} onChange={e => setForm({ ...form, transaction_amount: e.target.value })} className="mt-1" /></div>
        <div><Label className="text-xs font-semibold">Transaction Type</Label><Input value={form.transaction_type} onChange={e => setForm({ ...form, transaction_type: e.target.value })} placeholder="Wire transfer, cash..." className="mt-1" /></div>
        <div><Label className="text-xs font-semibold">Source of Funds</Label><Input value={form.source_of_funds} onChange={e => setForm({ ...form, source_of_funds: e.target.value })} placeholder="Employment, investment..." className="mt-1" /></div>
        <div><Label className="text-xs font-semibold">Occupation</Label><Input value={form.occupation} onChange={e => setForm({ ...form, occupation: e.target.value })} placeholder="Attorney, CEO..." className="mt-1" /></div>
        <div className="flex items-center gap-3 pt-5">
          <input type="checkbox" checked={form.pep_status} onChange={e => setForm({ ...form, pep_status: e.target.checked })} className="h-4 w-4 accent-red-600" data-testid="aml-pep" />
          <Label className="text-xs font-semibold">Politically Exposed Person (PEP)</Label>
        </div>
      </div>
      <div><Label className="text-xs font-semibold">Additional Context</Label><Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Any suspicious patterns..." className="mt-1 min-h-[60px]" /></div>
      <Button onClick={run} disabled={loading || !form.entity_name} className="w-full bg-gradient-to-r from-slate-800 to-blue-900 hover:opacity-90 h-11" data-testid="aml-check-btn">
        {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Checking...</> : <><Shield className="mr-2 h-4 w-4" />Run AML Compliance Check</>}
      </Button>

      {data && (
        <div className="space-y-5 animate-in fade-in duration-500">
          <div className="rounded-2xl p-6 text-white" style={{ background: data.compliance_status === "fail" ? "linear-gradient(135deg,#7F1D1D,#DC2626)" : data.compliance_status === "review" ? "linear-gradient(135deg,#78350F,#D97706)" : "linear-gradient(135deg,#065F46,#22C55E)" }}>
            <p className="text-xs uppercase tracking-widest opacity-80 mb-2">AML Compliance Status</p>
            <div className="flex items-center justify-center gap-6">
              <p className="text-5xl font-black uppercase">{data.compliance_status}</p>
              <div><p className="text-2xl font-bold">{data.aml_score}/100</p><p className="text-xs opacity-80">Risk Score</p></div>
            </div>
          </div>
          {data.flags?.length > 0 && (
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-red-600" />Compliance Flags</CardTitle></CardHeader>
              <CardContent><div className="space-y-2">{data.flags.map((f, i) => (
                <div key={i} className="p-3 rounded-xl bg-red-50 border border-red-100">
                  <div className="flex items-center justify-between"><span className="text-sm font-semibold text-red-800">{f.flag}</span><Badge className="bg-red-100 text-red-700">{f.score}</Badge></div>
                  <p className="text-xs text-red-600 mt-0.5">{f.detail}</p><p className="text-[10px] text-red-400 mt-0.5 font-mono">{f.regulation}</p>
                </div>
              ))}</div></CardContent>
            </Card>
          )}
          {data.required_filings?.length > 0 && (
            <Card className="border-0 shadow-sm border-l-4 border-l-blue-500">
              <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><FileText className="h-4 w-4 text-blue-600" />Required Filings</CardTitle></CardHeader>
              <CardContent><div className="space-y-2">{data.required_filings.map((f, i) => (
                <div key={i} className="flex items-center justify-between p-3 rounded-xl bg-blue-50">
                  <div><p className="text-sm font-semibold text-blue-800">{f.form}</p><p className="text-xs text-blue-600">Deadline: {f.deadline}</p></div>
                  <p className="text-[10px] text-blue-400 font-mono">{f.regulation}</p>
                </div>
              ))}</div></CardContent>
            </Card>
          )}
          {ai && (
            <div className="space-y-4">
              {ai.compliance_narrative && <Card className="border-0 shadow-sm bg-slate-50"><CardContent className="pt-5"><p className="text-sm text-slate-700">{ai.compliance_narrative}</p></CardContent></Card>}
              {ai.sanctions_screening && (
                <Card className="border-0 shadow-sm border-l-4 border-l-red-500">
                  <CardHeader className="pb-2"><CardTitle className="text-sm">Sanctions Screening</CardTitle></CardHeader>
                  <CardContent>
                    <Badge className={ai.sanctions_screening.clear ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}>{ai.sanctions_screening.clear ? "CLEAR" : "POTENTIAL MATCH"}</Badge>
                    {ai.sanctions_screening.lists_checked && <p className="text-xs text-slate-500 mt-2">Lists checked: {ai.sanctions_screening.lists_checked.join(", ")}</p>}
                  </CardContent>
                </Card>
              )}
              <div className="grid md:grid-cols-2 gap-4">
                {ai.enhanced_due_diligence && <Card className="border-0 shadow-sm border-l-4 border-l-blue-500"><CardHeader className="pb-2"><CardTitle className="text-sm">EDD Steps</CardTitle></CardHeader><CardContent><InsightList items={ai.enhanced_due_diligence} color="text-blue-500" /></CardContent></Card>}
                {ai.recommended_actions && <Card className="border-0 shadow-sm border-l-4 border-l-emerald-500"><CardHeader className="pb-2"><CardTitle className="text-sm">Recommended Actions</CardTitle></CardHeader><CardContent><InsightList items={ai.recommended_actions} color="text-emerald-500" /></CardContent></Card>}
              </div>
              {ai.confidence_score !== undefined && <div className="text-center"><Badge className="bg-slate-100 text-slate-600">AI Confidence: {ai.confidence_score}%</Badge></div>}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Main ──────────────────────────────────────────────────
const TripleShieldPage = () => (
  <div className="max-w-5xl mx-auto py-6 px-4 space-y-6" data-testid="triple-shield">
    <div className="rounded-2xl p-6 text-white" style={{ background: "linear-gradient(135deg, #1E293B, #0F172A)" }}>
      <div className="flex items-center gap-3 mb-1">
        <Shield className="h-7 w-7 text-blue-400" />
        <h1 className="text-2xl font-black tracking-tight">Triple Shield</h1>
        <Badge className="bg-blue-500/20 text-blue-200 border-blue-500/30">COMPLIANCE</Badge>
      </div>
      <p className="text-slate-300 text-sm">SelleShield fraud/risk scoring + AML compliance center with sanctions screening</p>
    </div>
    <Tabs defaultValue="selleshield" className="space-y-4">
      <TabsList className="grid w-full grid-cols-2 h-auto p-1">
        <TabsTrigger value="selleshield" className="text-xs py-2 data-[state=active]:bg-red-600 data-[state=active]:text-white"><ShieldAlert className="h-3.5 w-3.5 mr-1.5" />SelleShield</TabsTrigger>
        <TabsTrigger value="aml" className="text-xs py-2 data-[state=active]:bg-slate-800 data-[state=active]:text-white"><Shield className="h-3.5 w-3.5 mr-1.5" />AML Compliance</TabsTrigger>
      </TabsList>
      <TabsContent value="selleshield"><SelleShieldTab /></TabsContent>
      <TabsContent value="aml"><AMLTab /></TabsContent>
    </Tabs>
  </div>
);

export default TripleShieldPage;
